import React from 'react';
import { GlassCard } from './ui/glass-card';
import { GlassButton } from './ui/glass-button';
import { Star, Heart, ShoppingCart, Sparkles, Zap, Rocket, Crown } from 'lucide-react';

const GlassShowcase = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 p-8">
      <div className="container-glass">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-6xl font-bold gradient-text mb-4 animate-fade-in text-hover-rainbow">
            Modern Glass Design System
          </h1>
          <p className="text-xl text-muted-foreground animate-slide-up text-hover-glow">
            Experience the future of UI with glassmorphism effects and advanced hover interactions
          </p>
        </div>

        {/* Enhanced Hover Effects Demo */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold gradient-text mb-8 text-center">Advanced Hover Effects</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Tilt Effect */}
            <GlassCard variant="feature" className="hover-tilt animate-slide-up">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center mx-auto mb-4 micro-bounce">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2">Tilt Effect</h3>
                <p className="text-sm text-muted-foreground">3D perspective tilt on hover</p>
              </div>
            </GlassCard>

            {/* Magnetic Effect */}
            <GlassCard variant="feature" className="hover-magnetic animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-blue-500 to-cyan-600 flex items-center justify-center mx-auto mb-4 hover-pulse">
                  <Zap className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2">Magnetic</h3>
                <p className="text-sm text-muted-foreground">Smooth magnetic attraction</p>
              </div>
            </GlassCard>

            {/* Bounce Effect */}
            <GlassCard variant="feature" className="hover-bounce animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-600 flex items-center justify-center mx-auto mb-4 micro-wiggle">
                  <Rocket className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2">Bounce</h3>
                <p className="text-sm text-muted-foreground">Playful bounce animation</p>
              </div>
            </GlassCard>

            {/* Pulse Glow */}
            <GlassCard variant="feature" className="hover-pulse animate-slide-up" style={{ animationDelay: '0.3s' }}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-yellow-500 to-orange-600 flex items-center justify-center mx-auto mb-4 hover-glow">
                  <Crown className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold mb-2">Pulse Glow</h3>
                <p className="text-sm text-muted-foreground">Pulsing glow effect</p>
              </div>
            </GlassCard>
          </div>
        </div>

        {/* Interactive Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {/* Enhanced Product Card */}
          <GlassCard variant="default" className="interactive-card animate-scale-in">
            <div className="aspect-square bg-gradient-to-br from-pink-400 to-purple-600 rounded-2xl mb-4 flex items-center justify-center image-zoom overflow-hidden">
              <Heart className="w-12 h-12 text-white animate-float hover-bounce" />
            </div>
            <h3 className="font-semibold mb-2 text-hover-glow">Premium Product</h3>
            <div className="flex items-center gap-1 mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400 hover-bounce" />
              ))}
              <span className="text-sm text-muted-foreground ml-2">(4.9)</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold price-tag hover-glow">₹2,999</span>
              <GlassButton variant="primary" size="sm" className="btn-float">
                <ShoppingCart className="w-4 h-4" />
              </GlassButton>
            </div>
          </GlassCard>

          {/* Shine Effect Card */}
          <GlassCard variant="gradient" className="hover-shine animate-scale-in" style={{ animationDelay: '0.1s' }}>
            <div className="text-center">
              <div className="text-4xl font-bold gradient-text mb-2 micro-pulse">10K+</div>
              <div className="text-muted-foreground mb-4">Happy Customers</div>
              <div className="w-full bg-white/20 rounded-full h-2 mb-4 overflow-hidden">
                <div className="bg-gradient-to-r from-pink-500 to-purple-600 h-2 rounded-full w-3/4 hover-gradient-shift"></div>
              </div>
              <div className="text-sm text-muted-foreground">Growing every day</div>
            </div>
          </GlassCard>

          {/* Border Draw Effect */}
          <GlassCard variant="light" className="hover-border-draw animate-scale-in" style={{ animationDelay: '0.2s' }}>
            <div className="text-center">
              <h3 className="text-xl font-bold mb-4 text-hover-glow">Border Animation</h3>
              <p className="text-muted-foreground mb-4">Watch the border draw on hover</p>
              <GlassButton variant="glass" className="interactive-button">
                Hover Me
              </GlassButton>
            </div>
          </GlassCard>
        </div>

        {/* Enhanced Button Showcase */}
        <GlassCard variant="light" className="mb-16 card-padding animate-bounce-in glass-hover-intense">
          <h2 className="text-3xl font-bold mb-8 text-center gradient-text">Interactive Buttons</h2>
          <div className="flex flex-wrap gap-6 justify-center">
            <GlassButton variant="primary" glow className="btn-ripple btn-float">
              Ripple Effect
            </GlassButton>
            <GlassButton variant="secondary" className="hover-magnetic">
              Magnetic Hover
            </GlassButton>
            <GlassButton variant="glass" className="hover-shine">
              Shine Effect
            </GlassButton>
            <GlassButton variant="outline" className="hover-border-draw">
              Border Draw
            </GlassButton>
          </div>
        </GlassCard>

        {/* Text Effects Showcase */}
        <GlassCard variant="medium" className="mb-16 card-padding animate-slide-in-right">
          <h2 className="text-3xl font-bold mb-8 text-center gradient-text">Text Effects</h2>
          <div className="space-y-6 text-center">
            <h3 className="text-2xl font-bold text-hover-glow cursor-pointer">
              Hover for Glow Effect
            </h3>
            <h3 className="text-2xl font-bold text-hover-rainbow cursor-pointer">
              Rainbow Animation Text
            </h3>
            <div className="text-lg font-mono text-hover-typewriter cursor-pointer">
              Typewriter Effect on Hover
            </div>
          </div>
        </GlassCard>

        {/* Image Effects */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <GlassCard className="image-zoom overflow-hidden animate-slide-in-left">
            <div className="aspect-video bg-gradient-to-br from-blue-400 to-purple-600 rounded-t-3xl flex items-center justify-center">
              <span className="text-white font-bold">Zoom Effect</span>
            </div>
            <div className="p-4">
              <h3 className="font-bold">Image Zoom</h3>
              <p className="text-sm text-muted-foreground">Smooth zoom on hover</p>
            </div>
          </GlassCard>
          
          <GlassCard className="image-parallax overflow-hidden animate-slide-up">
            <div className="aspect-video bg-gradient-to-br from-green-400 to-blue-600 rounded-t-3xl flex items-center justify-center">
              <span className="text-white font-bold">Parallax Effect</span>
            </div>
            <div className="p-4">
              <h3 className="font-bold">Image Parallax</h3>
              <p className="text-sm text-muted-foreground">Parallax movement</p>
            </div>
          </GlassCard>
          
          <GlassCard className="image-blur-focus overflow-hidden animate-slide-in-right">
            <div className="aspect-video bg-gradient-to-br from-pink-400 to-red-600 rounded-t-3xl flex items-center justify-center">
              <span className="text-white font-bold">Blur Focus</span>
            </div>
            <div className="p-4">
              <h3 className="font-bold">Blur Focus</h3>
              <p className="text-sm text-muted-foreground">Focus blur effect</p>
            </div>
          </GlassCard>
        </div>

        {/* Micro-interactions */}
        <GlassCard variant="heavy" className="text-center card-padding animate-fade-in">
          <h2 className="text-4xl font-bold gradient-text mb-6">
            Micro-interactions
          </h2>
          <div className="flex justify-center gap-8 mb-6">
            <div className="micro-bounce text-2xl">🎯</div>
            <div className="micro-pulse text-2xl">💎</div>
            <div className="micro-wiggle text-2xl">🚀</div>
          </div>
          <p className="text-muted-foreground">
            Subtle animations that bring your interface to life
          </p>
        </GlassCard>
      </div>
    </div>
  );
};

export default GlassShowcase;
